import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { LuRadio } from "react-icons/lu";
import { Button } from "@/components/ui/button";
import { WeekDayCycler } from "@/components/v2/home/WeekDayCycler";
import { HomePhaseShell } from "@/components/v2/home/HomePhaseShell";
import type { DayAvailabilitySummary } from "@/lib/api/availability";
import { parseISO } from "date-fns";
import { dayKeyFromDate } from "@/lib/v2/pollAggregates";

interface HomeIdleCardProps {
  isAdmin: boolean;
  readOnly?: boolean;
  selectedDates: string[];
  availabilitySummary: DayAvailabilitySummary[];
  pendingDates: string[];
  openingPoll: boolean;
  onToggleDay: (date: string) => void;
  onOpenPoll: () => void;
  headerAction?: ReactNode;
}

export function HomeIdleCard({
  isAdmin,
  readOnly = false,
  selectedDates,
  availabilitySummary,
  pendingDates,
  openingPoll,
  onToggleDay,
  onOpenPoll,
  headerAction,
}: HomeIdleCardProps) {
  const { t } = useTranslation();

  const selected = selectedDates.map((d) => parseISO(`${d}T12:00:00`));
  const availabilityMap = Object.fromEntries(
    availabilitySummary.map((d) => [d.date, d.count])
  );

  return (
    <HomePhaseShell
      eyebrow={t("v2.home.phaseEyebrowIdle")}
      title={t("v2.home.phaseTitleIdle")}
      headerAction={headerAction}
      adminSection={
        isAdmin ? (
          <Button
            className="w-full min-h-[48px] bg-amber-600 text-white hover:bg-amber-600/90 font-bold shadow-sm"
            disabled={openingPoll}
            onClick={onOpenPoll}
          >
            <LuRadio className="h-4 w-4" />
            {openingPoll ? t("common.loading") : t("v2.home.openPollShort")}
          </Button>
        ) : undefined
      }
      adminZone={
        isAdmin
          ? {
              title: t("v2.home.adminZoneOpenPollTitle"),
              description: t("v2.home.adminZoneOpenPollDesc"),
              actionRequired: true,
            }
          : undefined
      }
    >
      <div className="space-y-2">
        <div>
          <p className="text-sm font-semibold">{t("v2.home.optionalAvailabilityTitle")}</p>
          <p className="text-sm text-muted-foreground">
            {t("v2.home.optionalAvailabilityHint")}
          </p>
        </div>
        <WeekDayCycler
          selected={selected}
          onChange={(dates) => {
            const newKeys = new Set(dates.map(dayKeyFromDate));
            const oldKeys = new Set(selectedDates);
            for (const key of new Set([...newKeys, ...oldKeys])) {
              if (newKeys.has(key) !== oldKeys.has(key)) {
                onToggleDay(key);
                return;
              }
            }
          }}
          showAvailability={isAdmin}
          availability={availabilityMap}
          disabled={readOnly}
          pendingDates={pendingDates}
          compact
        />
      </div>
    </HomePhaseShell>
  );
}
